import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Home, Phone } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLanguage } from "../contexts/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const LABELS = {
  tr: {
    title: "Teşekkürler!",
    subtitle: "Mesajınız başarıyla alındı.",
    desc: "En kısa sürede, mesai saatleri içinde (Pzt–Cum 09:30–18:30) size dönüş yapacağız. Acil bir konunuz varsa bizi doğrudan arayabilirsiniz.",
    home: "Ana Sayfaya Dön",
    contact: "İletişim Sayfası",
    phone: "+90 (850) 888 48 65",
    meta_title: "Teşekkürler — Yastık Medya",
    badge: "Form Gönderildi",
  },
  en: {
    title: "Thank You!",
    subtitle: "Your message has been received.",
    desc: "We'll get back to you as soon as possible during business hours (Mon–Fri 09:30–18:30). For urgent matters, feel free to call us directly.",
    home: "Back to Home",
    contact: "Contact Page",
    phone: "+90 (850) 888 48 65",
    meta_title: "Thank You — Yastık Medya",
    badge: "Form Submitted",
  },
  es: {
    title: "¡Gracias!",
    subtitle: "Tu mensaje fue recibido.",
    desc: "Nos pondremos en contacto contigo lo antes posible durante el horario de oficina (Lun–Vie 09:30–18:30).",
    home: "Volver al Inicio",
    contact: "Página de Contacto",
    phone: "+90 (850) 888 48 65",
    meta_title: "Gracias — Yastık Medya",
    badge: "Formulario Enviado",
  },
  de: {
    title: "Danke!",
    subtitle: "Ihre Nachricht wurde empfangen.",
    desc: "Wir melden uns so schnell wie möglich während der Geschäftszeiten (Mo–Fr 09:30–18:30).",
    home: "Zur Startseite",
    contact: "Kontaktseite",
    phone: "+90 (850) 888 48 65",
    meta_title: "Danke — Yastık Medya",
    badge: "Formular Gesendet",
  },
  fr: {
    title: "Merci !",
    subtitle: "Votre message a été reçu.",
    desc: "Nous vous répondrons dans les plus brefs délais pendant les heures d'ouverture (Lun–Ven 09:30–18:30).",
    home: "Retour à l'accueil",
    contact: "Page de contact",
    phone: "+90 (850) 888 48 65",
    meta_title: "Merci — Yastık Medya",
    badge: "Formulaire Soumis",
  },
  ar: {
    title: "شكراً!",
    subtitle: "تم استلام رسالتك بنجاح.",
    desc: "سنتواصل معك في أقرب وقت ممكن خلال ساعات العمل (الاثنين–الجمعة 09:30–18:30).",
    home: "العودة إلى الرئيسية",
    contact: "صفحة الاتصال",
    phone: "+90 (850) 888 48 65",
    meta_title: "شكراً — يسطق ميديا",
    badge: "تم إرسال النموذج",
  },
};

export default function ThankYouPage() {
  const { lang } = useLanguage();
  const L = LABELS[lang] || LABELS.en;

  // Fire GTM/GA4 conversion event
  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "form_submission", {
        event_category: "Lead",
        event_label: "Contact Form",
        value: 1,
      });
    }
    if (window.dataLayer) {
      window.dataLayer.push({ event: "form_submit_success" });
    }
  }, []);

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="thank-you-page">
      <Helmet>
        <title>{L.meta_title}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />

      <main className="flex flex-col items-center justify-center py-24 sm:py-32">
        <div className="container-page max-w-lg text-center">
          {/* Success icon */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
            className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 border-4 border-emerald-100"
          >
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
          </motion.div>

          {/* Badge */}
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-[0.18em] px-4 py-1.5 mb-6"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {L.badge}
          </motion.span>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
            className="font-display text-display-1 font-bold text-ink leading-[0.95]"
          >
            {L.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-4 text-h3 font-display font-semibold text-brand-pink"
          >
            {L.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-5 text-body-lg text-ink/65 leading-relaxed"
          >
            {L.desc}
          </motion.p>

          {/* Phone */}
          <motion.a
            href={`tel:${L.phone.replace(/\D/g, "")}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mt-6 inline-flex items-center gap-2 text-brand-pink font-semibold hover:text-brand-magenta transition-colors"
          >
            <Phone className="h-4 w-4" />
            {L.phone}
          </motion.a>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to={`/${lang}/`}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-sm font-semibold hover:bg-brand-pink transition-colors"
            >
              <Home className="h-4 w-4" />
              {L.home}
            </Link>
            <Link
              to={`/${lang}/contact`}
              className="inline-flex items-center gap-2 rounded-full border-2 border-ink/20 px-6 py-3 text-sm font-semibold text-ink hover:border-ink transition-colors"
            >
              {L.contact}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
